import { useState } from "react";
import { KeyRound, UserPlus, Mail } from "lucide-react";

export default function AdminMemberships({ ctx }) {
  const { memberships, sendPasswordReset, grantMembership, sendWelcomeBackfill } = ctx;
  const [email, setEmail] = useState("");
  const [months, setMonths] = useState(12);

  const add = () => grantMembership(email, months).then(() => setEmail(""));

  const pendingWelcome = memberships.filter(
    (m) => !m.welcome_email_sent && (m.status === "active" || m.status === "trialing")
  ).length;

  return (
    <div className="mt-10 space-y-6" data-testid="admin-memberships">
      {/* Manually add / comp a member */}
      <div className="p-6 rounded-2xl bg-white border border-stone-200">
        <h3 className="font-serif font-black text-2xl text-[#121212] inline-flex items-center gap-2">
          <UserPlus size={20} /> Add a member manually
        </h3>
        <p className="text-sm text-stone-500 mt-1">
          Grants a free (comp) membership to an existing account — Vault access + monthly free Open Studio visit. The person must already have an account (have them sign up first if not).
        </p>
        <div className="mt-4 flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <label className="text-xs tracking-[0.2em] uppercase text-stone-500 block mb-1">Member email</label>
            <input
              data-testid="grant-member-email"
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="name@email.com"
              className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-[#121212] focus:outline-none"
            />
          </div>
          <div className="w-full sm:w-40">
            <label className="text-xs tracking-[0.2em] uppercase text-stone-500 block mb-1">Free months</label>
            <input
              data-testid="grant-member-months"
              type="number" min="1" max="120" value={months} onChange={(e) => setMonths(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-[#121212] focus:outline-none"
            />
          </div>
          <button
            data-testid="grant-member-btn"
            onClick={add}
            className="px-6 py-2.5 bg-[#121212] text-[#F2E9D8] hover:bg-black text-xs tracking-[0.2em] uppercase font-bold transition-all shrink-0"
          >
            Add member
          </button>
        </div>
      </div>

      {/* Welcome email backfill */}
      <div className="p-6 rounded-2xl bg-[#121212] text-[#F2E9D8] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="font-serif font-black text-2xl inline-flex items-center gap-2"><Mail size={20} className="text-[#C6FF33]" /> Welcome emails</h3>
          <p className="text-sm text-[#F2E9D8]/75 mt-1">
            New members get a welcome email automatically. Send it to existing active members who haven't received one yet.
            {pendingWelcome > 0 ? ` (${pendingWelcome} pending)` : " (all caught up)"}
          </p>
        </div>
        <button
          data-testid="send-welcome-backfill-btn"
          onClick={sendWelcomeBackfill}
          className="px-6 py-3 bg-[#C6FF33] text-[#121212] hover:bg-[#b6ef23] text-xs tracking-[0.2em] uppercase font-bold transition-all shrink-0"
        >
          Send welcome to existing members
        </button>
      </div>

      <div className="space-y-2">
        {memberships.length === 0 ? <p className="text-[#121212]/60">No memberships yet.</p> :
          memberships.map((m) => (
            <div key={m.id} className="p-4 bg-white border-2 border-[#121212] flex justify-between gap-4 flex-wrap items-center">
              <div>
                <p className="font-medium text-stone-900">{m.user_name} · {m.user_email}</p>
                <p className="text-sm text-stone-500">
                  {m.plan} · Free visits used this period: {m.freebies_used_this_period ?? 0}/1
                  {m.current_period_end && ` · renews ${new Date(m.current_period_end).toLocaleDateString()}`}
                  {m.welcome_email_sent && " · welcomed ✓"}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid={`send-reset-${m.id}`}
                  onClick={() => sendPasswordReset(m.user_email)}
                  title="Email this member a password reset link"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border-2 border-[#121212] text-[#121212] hover:bg-[#121212] hover:text-[#F2E9D8] text-[11px] tracking-[0.15em] uppercase font-bold transition-colors"
                >
                  <KeyRound size={13} /> Send password reset
                </button>
                <span className={`px-3 py-1 text-xs tracking-[0.15em] uppercase font-bold h-fit border-2 border-[#121212] ${
                  m.status === "active" ? "bg-[#C6FF33]" : "bg-stone-100"
                }`}>{m.cancel_at_period_end ? "ending" : (m.status || "—")}</span>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

